import { Metamodel } from 'app/model/base/model';
import { SapOdpDataset } from 'app/model/dataset/sap-odp.dataset';
import { DataFlowDatasetSettings, DatasetFormatType } from './dataflow-dataset-settings';
import { DataFlowSource } from 'app/model/dataflow/dataflow-source';
import { format } from 'app/shared/utils/string.util';
import { SapOdpLinkedService } from 'app/model/linkedservice/sap-odp.linkedservice';
import { DataFlowExpressionArray } from 'app/model/dataflow/dataflow-expression-array';
import { adfModelTypeMap } from 'app/registries/consts/adf-model-type-map-const';
import { IViolation, ReadBehavior } from 'shell-models-lib';
import { DataFlowExpression } from '../dataflowexpression';
import { BooleanDataFlowType, StringDataFlowType } from '../dataflow-datatype';
import { propertyIsNullOrEmpty } from 'app/model/adapters/linkedservices/linked-service-adapter.util';

export enum ReadMode {
    FULL_LOAD = 'fullLoad',
    INCREMENTAL_LOAD = 'incrementalLoad',
    FULL_AND_INCREMENTAL_LOAD = 'fullAndIncrementalLoad'
}

@Metamodel.model({
    name: adfModelTypeMap.sapOdp.dataflowSettings
})
export class SapOdpSettings extends DataFlowDatasetSettings {
    private static validateReadMode(owner: SapOdpSettings, value: DataFlowExpression): IViolation[] {
        const violations: IViolation[] = [];
        const source = owner.owner;
        if (
            source instanceof DataFlowSource &&
            source.isInlineDataset &&
            !value.templateExpression &&
            value.expression !== ReadMode.FULL_LOAD &&
            source.sourceLinkedService instanceof SapOdpLinkedService &&
            !source.sourceLinkedService.fullyByJsonEditor &&
            propertyIsNullOrEmpty(source.sourceLinkedService, SapOdpLinkedService.SUBSCRIBER_NAME)
        ) {
            violations.push({
                message: () =>
                    format(
                        stringResources.Validation_SapCDCSubscriberNameRequired,
                        source.sourceLinkedService.name,
                        value.expression === ReadMode.FULL_AND_INCREMENTAL_LOAD
                            ? stringResources.DataFlowFullOnFirstRunThenIncremental
                            : stringResources.DataFlowIncrementalChangesOnly
                    ),
                redirectTo: {
                    entity: source.sourceLinkedService,
                    attribute: SapOdpLinkedService.SUBSCRIBER_NAME
                }
            });
        }
        return violations;
    }

    private static validateKeyColumns(owner: SapOdpSettings, value: DataFlowExpressionArray): IViolation[] {
        const violations: IViolation[] = [];
        const source = owner.owner;
        if (
            source instanceof DataFlowSource &&
            source.isInlineDataset &&
            !value?.templateExpression &&
            !value?.expressions.length &&
            !owner.readMode.templateExpression &&
            owner.readMode.expression !== ReadMode.FULL_LOAD
        ) {
            violations.push({
                message: () => format(stringResources.RequiredTemplate, stringResources.DataFlowDatasetKeyColumns)
            });
        }
        return violations;
    }

    @Metamodel.attribute({})
    public context: DataFlowExpression;
    @Metamodel.attribute({})
    public objectName: DataFlowExpression;
    @Metamodel.attribute({
        constraint: {
            validator: SapOdpSettings.validateReadMode
        }
    })
    public readMode: DataFlowExpression;
    @Metamodel.attribute({})
    public browseContext: DataFlowExpression;
    @Metamodel.attribute({
        display: stringResources.DataFlowDatasetKeyColumns,
        constraint: {
            validator: SapOdpSettings.validateKeyColumns
        }
    })
    public sapOdpKeyColumns: DataFlowExpressionArray;
    public fixDecimalPrecision: boolean = true;
    public legacyMode: boolean = false;

    public get runMode(): ReadBehavior {
        switch (this.readMode.expression) {
            case ReadMode.FULL_LOAD:
                return ReadBehavior.FULL_ON_EVERY_RUN;
            case ReadMode.FULL_AND_INCREMENTAL_LOAD:
                return ReadBehavior.FULL_ON_FIRST_RUN_THEN_INCREMENTAL;
            case ReadMode.INCREMENTAL_LOAD:
                return ReadBehavior.INCREMENTAL_CHANGES_ONLY;
            default:
                return undefined;
        }
    }

    public getDatasetType(): string {
        return Metamodel.modelFor(SapOdpDataset).name;
    }

    public getFormat(): DatasetFormatType {
        return DatasetFormatType.SapODP;
    }

    public getSubFormat(): string {
        return undefined;
    }

    public getIconClass(): string {
        return 'i-100x100-Sap';
    }

    public initialize(subFormat: string): void {
        this.context = this.transform.createTemplatableExpression();
        this.objectName = this.transform.createTemplatableExpression();
        this.readMode = this.transform.createTemplatableExpression(StringDataFlowType.TYPE, ReadMode.FULL_AND_INCREMENTAL_LOAD);
        this.browseContext = this.transform.createTemplatableExpression(BooleanDataFlowType.TYPE, 'true');
        this.sapOdpKeyColumns = this.transform.createTemplatableExpressionArray();
    }
}
